import React from "react";
import { Alert, Button } from "@mui/material";

export type WorkflowCaseActionBannerKind =
  | "conflict"
  | "forbidden"
  | "not-found"
  | "invalid"
  | "failed"
  | "reload-failed";

interface WorkflowCaseActionBannerProps {
  kind: WorkflowCaseActionBannerKind | null;
  /** What "not yours" means on this stage — e.g. only the pause owner may resume. */
  forbiddenMessage: string;
  /** The engine's own sentence for a 422, when it gave one. */
  invalidMessage?: string | null;
  onReload: () => void;
}

/** The one banner a Task / Pause / Cancel stage on a real case shows when an action didn't land. */
const WorkflowCaseActionBanner: React.FC<WorkflowCaseActionBannerProps> = ({
  kind,
  forbiddenMessage,
  invalidMessage,
  onReload,
}) => {
  if (!kind) return null;
  const reloadAction = (
    <Button color="inherit" size="small" onClick={onReload}>
      Reload
    </Button>
  );

  switch (kind) {
    case "conflict":
      return (
        <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
          This case was updated by someone else. Reload to see the latest.
        </Alert>
      );
    case "reload-failed":
      return (
        <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
          That was recorded, but the case couldn&apos;t be reloaded.
        </Alert>
      );
    case "forbidden":
      return (
        <Alert severity="error" className="workflow-run__alert">
          {forbiddenMessage}
        </Alert>
      );
    case "not-found":
      return (
        <Alert severity="error" className="workflow-run__alert" action={reloadAction}>
          This case or step no longer exists.
        </Alert>
      );
    case "invalid":
      return (
        <Alert severity="error" className="workflow-run__alert">
          {invalidMessage || "The workflow engine refused that action."}
        </Alert>
      );
    default:
      return (
        <Alert severity="error" className="workflow-run__alert">
          That didn&apos;t go through — try again.
        </Alert>
      );
  }
};

export default WorkflowCaseActionBanner;
